import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useServicosAtivos } from '@/hooks/useServicos';
import type { Servico, ItemSelecionado } from '@/types/servico';
import { 
  formatarValor, 
  formatarDescricaoPreco, 
  calcularTotalItens,
} from '@/types/servico';
import { 
  Package, 
  Plus, 
  Minus, 
  Loader2,
  AlertCircle,
} from 'lucide-react';

interface SeletorServicosProps {
  value: ItemSelecionado[];
  onChange: (itens: ItemSelecionado[]) => void;
}

export function SeletorServicos({ value, onChange }: SeletorServicosProps) {
  const { t } = useTranslation();
  const { data: servicos, isLoading } = useServicosAtivos();

  // Adicionar serviço à lista
  const adicionarServico = (servico: Servico) => {
    const existente = value.find(i => i.servico.id === servico.id);
    
    if (existente) {
      // Se já existe, incrementar quantidade
      onChange(value.map(i => 
        i.servico.id === servico.id 
          ? { ...i, quantidade: i.quantidade + 1 }
          : i
      ));
    } else {
      // Se não existe, adicionar com quantidade 1
      onChange([...value, { servico, quantidade: 1 }]);
    }
  };

  // Remover serviço da lista
  const removerServico = (servicoId: string) => {
    const existente = value.find(i => i.servico.id === servicoId);
    
    if (existente && existente.quantidade > 1) {
      // Se tem mais de 1, decrementar
      onChange(value.map(i => 
        i.servico.id === servicoId 
          ? { ...i, quantidade: i.quantidade - 1 }
          : i
      ));
    } else {
      // Se tem 1 ou menos, remover
      onChange(value.filter(i => i.servico.id !== servicoId));
    }
  };

  // Atualizar quantidade diretamente
  const atualizarQuantidade = (servicoId: string, quantidade: number) => {
    if (quantidade <= 0) {
      onChange(value.filter(i => i.servico.id !== servicoId));
    } else {
      onChange(value.map(i => 
        i.servico.id === servicoId 
          ? { ...i, quantidade }
          : i
      ));
    }
  };

  // Verificar se serviço está selecionado
  const getQuantidade = (servicoId: string) => {
    const item = value.find(i => i.servico.id === servicoId);
    return item?.quantidade || 0;
  };

  // Calcular total
  const total = calcularTotalItens(value);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!servicos || servicos.length === 0) {
    return (
      <Card className="border-dashed">
        <CardContent className="flex items-center gap-3 py-6">
          <AlertCircle className="h-5 w-5 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            {t('orders.form.noServicesAvailable')}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Lista de serviços disponíveis */}
      <div className="space-y-2">
        {servicos.map((servico) => {
          const quantidade = getQuantidade(servico.id);
          const selecionado = quantidade > 0;
          const subtotal = servico.valor_base * quantidade;
          const isPorHora = servico.tipo_preco === 'por_hora';

          return (
            <div
              key={servico.id}
              className={`flex items-center justify-between p-3 border rounded-lg transition-all ${
                selecionado 
                  ? 'border-primary bg-primary/5' 
                  : 'hover:border-muted-foreground/30'
              }`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <Package className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium">{servico.nome}</span>
                </div>
                <p className="text-sm text-muted-foreground mt-0.5">
                  {formatarDescricaoPreco(servico, t)}
                </p>
              </div>

              <div className="flex items-center gap-3">
                {isPorHora ? (
                  <>
                    <span className="text-xs text-muted-foreground whitespace-nowrap">
                      {t('orders.calculatedByTime')}
                    </span>
                    <Button
                      type="button"
                      variant={selecionado ? 'secondary' : 'outline'}
                      size="sm"
                      onClick={() => (selecionado ? removerServico(servico.id) : adicionarServico(servico))}
                    >
                      {selecionado ? <Minus className="h-4 w-4 mr-1" /> : <Plus className="h-4 w-4 mr-1" />}
                      {selecionado ? t('common.remove') : t('common.add')}
                    </Button>
                  </>
                ) : selecionado ? (
                  <>
                    {/* Controles de quantidade */}
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => removerServico(servico.id)}
                      >
                        <Minus className="h-4 w-4" />
                      </Button>

                      <Input
                        type="number"
                        min="1"
                        value={quantidade}
                        onChange={(e) => atualizarQuantidade(
                          servico.id,
                          parseInt(e.target.value) || 0
                        )}
                        className="w-16 h-8 text-center"
                      />

                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => adicionarServico(servico)}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>

                    {/* Subtotal */}
                    <div className="text-right min-w-[80px]">
                      <Badge variant="secondary" className="font-mono">
                        {formatarValor(subtotal)}
                      </Badge>
                    </div>
                  </>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => adicionarServico(servico)}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    {t('common.add')}
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Total */}
      {value.length > 0 && (
        <Card className="bg-primary/5 border-primary">
          <CardContent className="flex items-center justify-between py-4">
            <div className="text-sm text-muted-foreground">
              {value.length === 1 ? t('orders.form.serviceSelected', { count: value.length }) : t('orders.form.servicesSelected', { count: value.length })}
            </div>
            <div className="text-right">
              <p className="text-xs text-muted-foreground">{t('common.total')}</p>
              <p className="text-xl font-bold text-primary">
                {formatarValor(total)}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Aviso */}
      <p className="text-xs text-muted-foreground text-center">
        {t('orders.valuesBasedOnPriceTable')}
      </p>
    </div>
  );
}
